import NextImage, { ImageProps as NextImageProps } from 'next/image';
import React, { HTMLAttributes, ImgHTMLAttributes, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import twMerge from 'utils/twMerge';
import vshImageLoader from 'utils/vshImageLoader';

type CustomImageProps = HTMLAttributes<HTMLImageElement> & {
    alt: string;
    src: NextImageProps['src'] | undefined | null;
    loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
    testId?: string;
} & Omit<NextImageProps, 'src'>;

const fallbackImageSrc = '/images/optimized-noimage.png';

const Image: FC<CustomImageProps> = ({
    testId,
    src,
    width,
    height,
    className,
    alt,
    title,
    fill,
    loading,
    ...props
}) => {
    let testIdentifier = testId ?? 'basic-image';

    if (!src) {
        testIdentifier = testIdentifier + '-empty';
    }

    const imageUrl = src ?? null;
    const [error, setError] = useState<SyntheticEvent<HTMLImageElement, Event> | null>(null);
    const shouldLoadFallbackImage = !!error || !imageUrl;
    const onError = useCallback((err: SyntheticEvent<HTMLImageElement, Event> | null) => setError(err), []);

    const finalImageUrl = shouldLoadFallbackImage ? fallbackImageSrc : imageUrl;

    useEffect(() => {
        setError(null);
    }, [src]);

    return (
        <NextImage
            alt={alt}
            className={twMerge('responsive-image block object-contain focus-visible:outline-none', className)}
            data-testid={testIdentifier}
            fill={fill}
            height={height}
            loader={vshImageLoader}
            loading={loading}
            src={finalImageUrl}
            title={title}
            width={width}
            onError={onError}
            {...props}
        />
    );
};

export default Image;
